// Run button + streaming output area.
// Editor spec §5 / UI_GUIDE §3.6.

import { useEffect, useRef } from "react";
import { Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRunAgentSkill } from "@/hooks/useRunAgentSkill";
import type { ValidationResult } from "@/lib/agent-skills/validation";

interface RunWithClaudeButtonProps {
  systemPrompt: string;
  validation: ValidationResult;
  /** When selectedFileId changes we abort any in-flight run. */
  selectedFileId: string | null;
}

export function RunWithClaudeButton({
  systemPrompt,
  validation,
  selectedFileId,
}: RunWithClaudeButtonProps) {
  const { run, abort, isStreaming, output, usage, latencyMs, quotaRemaining, error } =
    useRunAgentSkill();

  const outputRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  // Abort on selectedFileId change
  useEffect(() => {
    abort();
  }, [selectedFileId, abort]);

  // Auto-scroll to bottom only when already at bottom (UI_GUIDE §6)
  useEffect(() => {
    const el = outputRef.current;
    if (!el) return;

    if (isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [output]);

  function handleScroll() {
    const el = outputRef.current;
    if (!el) return;
    const threshold = 20;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }

  const hasErrors = validation.errors.length > 0;
  const quotaExhausted = quotaRemaining !== null && quotaRemaining <= 0;
  const disabled = hasErrors || quotaExhausted;

  function handleRun() {
    if (disabled || isStreaming) return;
    void run({ systemPrompt });
  }

  function getDisabledTitle() {
    if (hasErrors) return "Fix validation errors before running.";
    if (quotaExhausted) return "Daily quota exhausted — resets at 00:00 UTC.";
    return undefined;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          variant="default"
          className="flex-1"
          onClick={handleRun}
          disabled={disabled || isStreaming}
          title={getDisabledTitle()}
        >
          <Play className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
          {isStreaming ? "Running…" : "Run"}
        </Button>

        {isStreaming && (
          <Button variant="outline" size="sm" onClick={abort}>
            <Square className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
            Stop
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive font-mono">
          <span className="font-medium">{error.code}:</span> {error.message}
        </div>
      )}

      {(output || isStreaming) && (
        <div className="space-y-1">
          <div
            ref={outputRef}
            onScroll={handleScroll}
            className="bg-card border border-border rounded-md p-3 min-h-[120px] font-mono text-xs whitespace-pre-wrap overflow-auto max-h-[400px]"
          >
            {output}
            {isStreaming && <span className="animate-pulse">▋</span>}
          </div>

          {(usage !== null || quotaRemaining !== null || latencyMs !== null) && (
            <p className="text-xs text-muted-foreground">
              {quotaRemaining !== null && (
                <>
                  Quota remaining: {quotaRemaining.toLocaleString()} / 500,000 tokens
                  {latencyMs !== null && " · "}
                </>
              )}
              {latencyMs !== null && <>Latency: {latencyMs.toLocaleString()}ms</>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
